import { API_METHODS } from '@compass/client/constants/api-methods';
import type {
  Evaluation,
  EvaluationFinding,
  EvaluationFindingReview,
  EvaluationFindingReviewInput,
} from '@compass/client/types/evaluation';
import type { FeedbackInfo } from '@compass/client/types/feedback';
import { type Scorecard, ScoringType } from '@compass/client/types/scorecard';
import {
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronUp,
  CircleOff,
  FunnelIcon,
  Info,
  Loader2,
  PencilLine,
  Sparkles,
  X,
} from 'lucide-react';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import { useAPIPermission } from '@/hooks/usePermissions';
import {
  formatAggregateScore,
  formatIneligibilityReasonTitle,
  getIneligibilityReason,
} from '@/pages/qc/utils';
import { cn } from '@/utils';
import { formatDuration } from '@/utils/date';

import {
  appendEvidenceTimestampToken,
  buildStructuredEvidenceFromComposerText,
  tokenizeEvidenceComposerText,
} from '../evidenceComposer';
import { useEvaluationFeedback } from '../hooks/useFeedback';
import { useUpdateEvaluation } from '../hooks/useUpdateEvaluation';
import {
  computeScorecardSectionResults,
  type FindingEvidenceSnippet,
  getFindingEvidenceSnippets,
  getQcReviewAlerts,
  getQcScoreToneClasses,
  getQuestionAnswerLabel,
  getQuestionScoreLabel,
  getReviewerEffectiveFindings,
  INLINE_EVIDENCE_TIMESTAMP_PATTERN,
  LEGACY_MULTI_RANGE_TIMESTAMP_BLOCK_PATTERN,
  normalizeQuestionRawScore,
  type SectionQuestionResult,
} from '../qcDetail.utils';
import { QcEvidenceComposerInput } from './QcEvidenceComposerInput';
import { QcFeedbackPopover } from './QcFeedbackPopover';
import { QcScorecardHeader } from './QcScorecardHeader';
import { getEvaluationWorkflowBannerState, QcWorkflowStatusBanner } from './QcWorkflowStatusBanner';

const EMPTY_VALUE = '--';
const WEIGHT_TEXT_CLASS = 'text-sm font-semibold text-secondary-foreground';
const COMPACT_HEADER_GAP_PX = 16;
const COMPACT_HEADER_HEIGHT_FALLBACK_PX = 64;

type DraftAnswerOption = 'yes' | 'no' | 'partial';

interface FindingReviewDraft {
  answer: DraftAnswerOption;
  evidence: string;
  evidenceTouched?: boolean;
  metricKey: string;
  questionTitle: string;
  scoringType?: ScoringType;
}

const getDraftAnswerLabel = (answer: DraftAnswerOption) => {
  switch (answer) {
    case 'yes':
      return 'Yes';
    case 'no':
      return 'No';
    case 'partial':
      return 'Partial';
    default:
      return '--';
  }
};

const getAnswerToneClass = (answer: DraftAnswerOption) => {
  switch (answer) {
    case 'yes':
      return 'bg-positive text-positive-foreground border-positive-border';
    case 'partial':
      return 'bg-attention text-attention-foreground border-attention-border';
    default:
      return 'bg-critical text-critical-foreground border-critical-border';
  }
};

const getRawScoreForAnswer = (answer: DraftAnswerOption, scoringType?: ScoringType) => {
  if (scoringType === ScoringType.SCORING_TYPE_SCALE) {
    if (answer === 'yes') return 1;
    if (answer === 'partial') return 0.5;
    return 0;
  }

  return answer === 'yes' ? 1 : 0;
};

const getDefaultDraftAnswer = (
  finding: EvaluationFinding | undefined,
  scoringType?: ScoringType,
): DraftAnswerOption => {
  const currentAnswer = getQuestionAnswerLabel(finding, scoringType);
  if (currentAnswer === 'Yes') {
    return 'no';
  }
  if (currentAnswer === 'Partial') {
    return 'no';
  }
  return 'yes';
};

const getReviewAnswer = (
  review: EvaluationFindingReview | undefined,
  scoringType?: ScoringType,
): DraftAnswerOption | null => {
  if (review?.raw_score === undefined || review?.raw_score === null) {
    return null;
  }
  const normalized = normalizeQuestionRawScore(review.raw_score, scoringType);
  if (scoringType === ScoringType.SCORING_TYPE_SCALE) {
    if (normalized >= 1) return 'yes';
    if (normalized > 0) return 'partial';
    return 'no';
  }
  return normalized >= 1 ? 'yes' : 'no';
};

const getAnswerOptions = (scoringType?: ScoringType) => {
  return scoringType === ScoringType.SCORING_TYPE_SCALE
    ? ([
        { value: 'yes' as const, label: 'Yes' },
        { value: 'partial' as const, label: 'Partial' },
        { value: 'no' as const, label: 'No' },
      ] satisfies { value: DraftAnswerOption; label: string }[])
    : ([
        { value: 'yes' as const, label: 'Yes' },
        { value: 'no' as const, label: 'No' },
      ] satisfies { value: DraftAnswerOption; label: string }[]);
};

const buildReviewEvidence = (
  evidenceText: string,
  assetId?: string,
): EvaluationFindingReviewInput['evidence'] | undefined => {
  return buildStructuredEvidenceFromComposerText(evidenceText, assetId);
};

const buildFindingReviewInput = (
  draft: FindingReviewDraft,
  finding?: EvaluationFinding,
): EvaluationFindingReviewInput => {
  const rawScore = getRawScoreForAnswer(draft.answer, draft.scoringType);

  return {
    raw_score: rawScore,
    evidence: buildReviewEvidence(draft.evidence, finding?.evidence?.asset_id),
  };
};

const getScrollRoot = (element: HTMLElement | null): HTMLElement | null => {
  let current = element?.parentElement ?? null;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }

    current = current.parentElement;
  }

  return null;
};

const getQuestionScoreClassName = (rawScore?: number) => {
  if (rawScore === undefined || rawScore === null) {
    return 'text-muted-foreground';
  }

  if (rawScore >= 1) {
    return 'text-positive-foreground';
  }

  if (rawScore <= 0) {
    return 'text-critical-foreground';
  }

  return 'text-attention-foreground';
};

const getQuestionScoreBarClassName = (rawScore?: number) => {
  if (rawScore === undefined || rawScore === null) {
    return 'bg-muted-foreground';
  }

  if (rawScore >= 1) {
    return 'bg-positive-foreground';
  }

  if (rawScore <= 0) {
    return 'bg-critical-foreground';
  }

  return 'bg-attention-foreground';
};

const getDisplayScore = (score?: number) => {
  return score === undefined || score === null ? EMPTY_VALUE : formatAggregateScore(score);
};

/** Question scored below full marks or AI skipped it entirely. */
const isQuestionLostPoints = (item: SectionQuestionResult) =>
  !item.effectiveFinding ||
  normalizeQuestionRawScore(item.effectiveFinding.raw_score, item.question.scoring_type) < 1;

/** Question has reviewer feedback attached to its finding. */
const hasQuestionFeedback = (
  item: SectionQuestionResult,
  feedbackMap: Map<number, FeedbackInfo[]>,
) => {
  return item.findingIndex !== undefined && (feedbackMap.get(item.findingIndex)?.length ?? 0) > 0;
};

const hasQuestionReviewerChange = (
  item: SectionQuestionResult,
  findingReviews: Record<string, EvaluationFindingReview>,
) => {
  const metricKey = item.question.metric_key;
  return !!(metricKey && findingReviews[metricKey]);
};

interface EvidenceProps {
  sections: Array<{
    action?: React.ReactNode;
    key: string;
    label: string;
    snippets: FindingEvidenceSnippet[];
  }>;
  onEvidenceSeek?: (
    seekSeconds: number,
    highlightStartSeconds?: number,
    highlightEndSeconds?: number,
  ) => void;
}

const renderEvidenceTimestampLink = (
  label: string,
  seekSeconds: number,
  onEvidenceSeek?: (
    seekSeconds: number,
    highlightStartSeconds?: number,
    highlightEndSeconds?: number,
  ) => void,
  highlightStartSeconds?: number,
  highlightEndSeconds?: number,
) => {
  if (!onEvidenceSeek) {
    return <span>{label}</span>;
  }

  return (
    <button
      type="button"
      className="inline text-primary transition-colors hover:text-primary/80 hover:underline cursor-pointer"
      onClick={() => onEvidenceSeek(seekSeconds, highlightStartSeconds, highlightEndSeconds)}
    >
      {label}
    </button>
  );
};

const renderFallbackEvidenceRanges = (
  snippet: FindingEvidenceSnippet,
  onEvidenceSeek?: (
    seekSeconds: number,
    highlightStartSeconds?: number,
    highlightEndSeconds?: number,
  ) => void,
) => {
  if (snippet.spans.length === 0) {
    return null;
  }

  return (
    <>
      {snippet.text ? ' ' : null}
      {snippet.spans.map((span, index) => (
        <React.Fragment key={`${span.start}-${span.end}`}>
          {index > 0 ? ', ' : null}[
          {renderEvidenceTimestampLink(
            formatDuration(span.start),
            span.start,
            onEvidenceSeek,
            span.start,
            span.end,
          )}
          {span.end > span.start ? (
            <>
              {' - '}
              {renderEvidenceTimestampLink(
                formatDuration(span.end),
                span.end,
                onEvidenceSeek,
                span.end,
                span.end,
              )}
            </>
          ) : null}
          ]
        </React.Fragment>
      ))}
    </>
  );
};

const renderEvidenceSnippet = (
  snippet: FindingEvidenceSnippet,
  onEvidenceSeek?: (
    seekSeconds: number,
    highlightStartSeconds?: number,
    highlightEndSeconds?: number,
  ) => void,
) => {
  const text = snippet.text?.trim() || '';
  if (!text) {
    return renderFallbackEvidenceRanges(snippet, onEvidenceSeek) ?? 'Evidence span captured.';
  }

  const composerParts = tokenizeEvidenceComposerText(text);
  const hasComposerTimestamps = composerParts.some(part => part.type === 'timestamp');
  if (hasComposerTimestamps) {
    const content: React.ReactNode[] = [];

    composerParts.forEach((part, index) => {
      if (part.type === 'text') {
        content.push(part.text);
        return;
      }

      content.push(
        <React.Fragment key={`${part.start_seconds}-${index}`}>
          {renderEvidenceTimestampLink(
            part.text,
            part.start_seconds,
            onEvidenceSeek,
            part.start_seconds,
          )}
        </React.Fragment>,
      );
    });

    return <>{content}</>;
  }

  const matches = [...text.matchAll(INLINE_EVIDENCE_TIMESTAMP_PATTERN)];
  if (matches.length === 0) {
    // Legacy snippets may already embed multi-range timestamp blocks in the text.
    // Strip those before appending structured fallback pills below so the same
    // ranges do not render twice.
    const textWithoutLegacyMultiRangeBlock = text
      .replace(LEGACY_MULTI_RANGE_TIMESTAMP_BLOCK_PATTERN, '')
      .replace(/\s+([.,;:!?])/g, '$1')
      .replace(/\s{2,}/g, ' ')
      .trim();

    return (
      <>
        {textWithoutLegacyMultiRangeBlock}
        {renderFallbackEvidenceRanges(
          {
            ...snippet,
            text: textWithoutLegacyMultiRangeBlock,
          },
          onEvidenceSeek,
        )}
      </>
    );
  }

  const content: React.ReactNode[] = [];
  let lastIndex = 0;
  let usedSpanCount = 0;

  matches.forEach((match, index) => {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      content.push(text.slice(lastIndex, matchIndex));
    }

    const matchedSpan = snippet.spans[usedSpanCount];
    usedSpanCount += 1;

    if (!matchedSpan) {
      // Some mixed legacy snippets can contain more inline timestamp markers than
      // structured spans. Preserve the original text instead of rendering a
      // broken link when the span array runs out.
      content.push(match[0]);
    } else {
      content.push(
        <React.Fragment key={`${matchedSpan.start}-${matchedSpan.end}-${index}`}>
          [
          {renderEvidenceTimestampLink(
            match[1],
            matchedSpan.start,
            onEvidenceSeek,
            matchedSpan.start,
            matchedSpan.end,
          )}
          {match[2] ? (
            <>
              {' - '}
              {renderEvidenceTimestampLink(
                match[2],
                matchedSpan.end,
                onEvidenceSeek,
                matchedSpan.end,
                matchedSpan.end,
              )}
            </>
          ) : null}
          ]
        </React.Fragment>,
      );
    }

    lastIndex = matchIndex + match[0].length;
  });

  if (lastIndex < text.length) {
    content.push(text.slice(lastIndex));
  }

  const remainingSpans = snippet.spans.slice(usedSpanCount);
  if (remainingSpans.length > 0) {
    content.push(
      renderFallbackEvidenceRanges(
        {
          text: '',
          spans: remainingSpans,
        },
        onEvidenceSeek,
      ) ?? null,
    );
  }

  return <>{content}</>;
};

const EvidenceBlock: React.FC<EvidenceProps> = ({ sections, onEvidenceSeek }) => {
  const [open, setOpen] = useState(false);
  const visibleSections = sections.filter(section => section.snippets.length > 0);

  if (visibleSections.length === 0) {
    return (
      <p className="pt-2 text-xs text-muted-foreground">No evidence recorded for this finding.</p>
    );
  }

  return (
    <div className="pt-2">
      <Button
        type="button"
        variant="ghost"
        className="h-auto px-1 text-xs text-muted-foreground"
        onClick={() => setOpen(current => !current)}
      >
        {open ? (
          <ChevronUp className="mr-1.5 h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="mr-1.5 h-3.5 w-3.5" />
        )}
        Evidence
      </Button>
      {open && (
        <div className="mt-2 space-y-2">
          {visibleSections.map(section => (
            <div key={section.key} className="rounded-md border border-border/60 bg-background p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-xs font-semibold tracking-wide text-muted-foreground">
                  {section.label}
                </div>
                {section.action}
              </div>
              <div className="space-y-3">
                {section.snippets.map((snippet, index) => (
                  <div key={`${section.key}-${snippet.text}-${index}`}>
                    <p className="text-sm text-foreground/90">
                      {renderEvidenceSnippet(snippet, onEvidenceSeek)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Renders the feedback flag for a question row.
 *
 * Only rendered when a finding exists (i.e. the AI produced a result) so
 * feedback can be attached to that specific finding index.
 *
 * Questions the AI skipped (no finding) don't get a flag because the
 * feedback API identifies per-question feedback by `finding_index`.
 * Without one, feedback would be evaluation-level and collide with the
 * header-level evaluation feedback.  A future API change (e.g. keying
 * feedback by metric_key) would allow flagging skipped questions too.
 */
const renderQuestionFeedbackFlag = (
  evaluationId: string | undefined,
  findingIndex: number | undefined,
  feedbackByFindingIndex: Map<number, FeedbackInfo[]>,
  userNameById?: Map<string, string>,
): React.ReactNode => {
  if (!evaluationId || findingIndex === undefined) return null;

  return (
    <QcFeedbackPopover
      evaluationId={evaluationId}
      findingIndex={findingIndex}
      feedback={feedbackByFindingIndex.get(findingIndex)}
      userNameById={userNameById}
    />
  );
};

interface QcDetailScorecardTabProps {
  evaluation: Evaluation;
  scorecard?: Scorecard | null;
  scorecardLoading?: boolean;
  onEvidenceSeek?: (
    seekSeconds: number,
    highlightStartSeconds?: number,
    highlightEndSeconds?: number,
  ) => void;
  userNameById?: Map<string, string>;
  transcriptInsertTimestampSeconds?: number | null;
  onTranscriptInsertTimestampHandled?: () => void;
}

export const QcDetailScorecardTab: React.FC<QcDetailScorecardTabProps> = ({
  evaluation,
  scorecard,
  scorecardLoading = false,
  onEvidenceSeek,
  userNameById,
  transcriptInsertTimestampSeconds,
  onTranscriptInsertTimestampHandled,
}) => {
  const { evaluationFeedbacks, feedbacksByFindingIndex } = useEvaluationFeedback(evaluation.id);
  const updateMutation = useUpdateEvaluation();
  // Editing reviewer answers writes back via UpdateEvaluation, so gate every
  // edit affordance on evaluation:write for the current role (e.g. QC Reviewer
  // is read-only on evaluations and must not see these controls).
  const { hasPermission: canEditEvaluation } = useAPIPermission(
    API_METHODS.EvaluationService.UpdateEvaluation,
  );
  const compactSentinelRef = useRef<HTMLDivElement>(null);
  const compactHeaderRef = useRef<HTMLDivElement>(null);
  const transcriptPointerDownRef = useRef(false);
  const evidenceInputFocusedRef = useRef(false);
  const [isCompact, setIsCompact] = useState(false);
  const [compactHeaderHeight, setCompactHeaderHeight] = useState(0);
  const [lostPointsFilter, setLostPointsFilter] = useState(false);
  const [withFeedbackFilter, setWithFeedbackFilter] = useState(false);
  const [reviewerChangesFilter, setReviewerChangesFilter] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, FindingReviewDraft>>({});
  const [activeEvidenceMetricKey, setActiveEvidenceMetricKey] = useState<string | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const { isWorkflowProcessing } = getEvaluationWorkflowBannerState(evaluation);
  const draftEntries = useMemo(() => Object.values(drafts), [drafts]);
  // Memoized: a bare `|| {}` is a new object every render when the evaluation has
  // no reviews (e.g. ineligible evals). That busted every memo below and made the
  // `setOpenSections(sectionValues)` effect loop forever, starving React Router's
  // startTransition navigation — the URL changed but the page never left.
  const findingReviews = useMemo(
    () => evaluation.reviews?.findings || {},
    [evaluation.reviews?.findings],
  );
  const hasDraftValidationErrors = draftEntries.some(draft => !draft.evidence.trim());
  const reviewerEffectiveFindings = useMemo(
    () => getReviewerEffectiveFindings(evaluation.findings || [], findingReviews),
    [evaluation.findings, findingReviews],
  );

  const sectionResults = useMemo(
    () =>
      computeScorecardSectionResults(
        scorecard?.sections || [],
        evaluation.findings || [],
        evaluation,
        reviewerEffectiveFindings,
      ),
    // Intentionally narrow: only recompute when fields used by scoring/filtering change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      evaluation.findings,
      evaluation.call_direction,
      evaluation.metadata,
      reviewerEffectiveFindings,
      scorecard?.sections,
    ],
  );

  const sectionCards = useMemo(
    () =>
      sectionResults.map((result, index) => ({
        title: result.section.title || 'Untitled Section',
        score: result.score,
        scoredCount: result.scoredCount,
        sectionValue: result.section.id || `section-${index}`,
      })),
    [sectionResults],
  );

  useEffect(() => {
    if (!isWorkflowProcessing) {
      return;
    }
    setIsEditing(false);
    setIsConfirmDialogOpen(false);
    setActiveEvidenceMetricKey(null);
  }, [isWorkflowProcessing]);

  useEffect(() => {
    if (
      transcriptInsertTimestampSeconds === null ||
      transcriptInsertTimestampSeconds === undefined
    ) {
      return;
    }

    if (!isEditing) {
      return;
    }

    if (!activeEvidenceMetricKey) {
      onTranscriptInsertTimestampHandled?.();
      return;
    }

    setDrafts(current => {
      const draft = current[activeEvidenceMetricKey];
      if (!draft) {
        return current;
      }
      return {
        ...current,
        [activeEvidenceMetricKey]: {
          ...draft,
          evidence: appendEvidenceTimestampToken(draft.evidence, transcriptInsertTimestampSeconds),
        },
      };
    });
    onTranscriptInsertTimestampHandled?.();
    if (!evidenceInputFocusedRef.current) {
      setActiveEvidenceMetricKey(null);
    }
  }, [
    activeEvidenceMetricKey,
    isEditing,
    onTranscriptInsertTimestampHandled,
    transcriptInsertTimestampSeconds,
  ]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      transcriptPointerDownRef.current = !!target?.closest('.transcript-container');
    };

    const handlePointerUp = () => {
      transcriptPointerDownRef.current = false;
    };

    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('pointercancel', handlePointerUp, true);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('pointercancel', handlePointerUp, true);
    };
  }, []);

  const filterCounts = useMemo(() => {
    let lostPoints = 0;
    let withFeedback = 0;
    let reviewerChanges = 0;
    let total = 0;

    for (const section of sectionResults) {
      for (const item of section.questions) {
        total++;
        const isLost = isQuestionLostPoints(item);
        const hasFb = hasQuestionFeedback(item, feedbacksByFindingIndex);
        const hasReviewerChange = hasQuestionReviewerChange(item, findingReviews);
        if (isLost) lostPoints++;
        if (hasFb) withFeedback++;
        if (hasReviewerChange) reviewerChanges++;
      }
    }

    return { lostPoints, withFeedback, reviewerChanges, total };
  }, [sectionResults, feedbacksByFindingIndex, findingReviews]);

  const isAnyFilterActive = lostPointsFilter || withFeedbackFilter || reviewerChangesFilter;

  const filteredQuestionsBySection = useMemo(() => {
    return sectionResults.map(section => {
      if (!isAnyFilterActive) return section.questions;
      return section.questions.filter(item => {
        if (lostPointsFilter && !isQuestionLostPoints(item)) return false;
        if (withFeedbackFilter && !hasQuestionFeedback(item, feedbacksByFindingIndex)) return false;
        if (reviewerChangesFilter && !hasQuestionReviewerChange(item, findingReviews)) return false;
        return true;
      });
    });
  }, [
    sectionResults,
    isAnyFilterActive,
    lostPointsFilter,
    withFeedbackFilter,
    reviewerChangesFilter,
    feedbacksByFindingIndex,
    findingReviews,
  ]);

  const filteredQuestionCount = useMemo(
    () => filteredQuestionsBySection.reduce((count, section) => count + section.length, 0),
    [filteredQuestionsBySection],
  );

  const filterStatusText = useMemo(() => {
    if (!isAnyFilterActive) {
      return `Showing all ${filterCounts.total} scored questions`;
    }

    const labels = [
      lostPointsFilter ? 'lost points' : null,
      withFeedbackFilter ? 'feedback' : null,
      reviewerChangesFilter ? 'reviewer changes' : null,
    ].filter(Boolean);

    return `Showing ${filteredQuestionCount} ${
      filteredQuestionCount === 1 ? 'question' : 'questions'
    } matching ${labels.join(' + ')}`;
  }, [
    filterCounts.total,
    filteredQuestionCount,
    isAnyFilterActive,
    lostPointsFilter,
    reviewerChangesFilter,
    withFeedbackFilter,
  ]);

  const lastUpdatedByName =
    (evaluation.updated_by && userNameById?.get(evaluation.updated_by)) || null;

  const scrollToSection = (sectionValue: string) => {
    setOpenSections(prev => (prev.includes(sectionValue) ? prev : [...prev, sectionValue]));
    requestAnimationFrame(() => {
      document.getElementById(sectionValue)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const sectionValues = useMemo(
    () =>
      sectionResults.map(
        (sectionResult, sectionIndex) => sectionResult.section.id || `section-${sectionIndex}`,
      ),
    [sectionResults],
  );
  const [openSections, setOpenSections] = useState<string[]>([]);
  const alerts = getQcReviewAlerts(evaluation);
  const allSectionsExpanded =
    sectionValues.length > 0 && openSections.length === sectionValues.length;

  useEffect(() => {
    setOpenSections(sectionValues);
  }, [sectionValues]);

  useLayoutEffect(() => {
    if (scorecardLoading) {
      return;
    }

    const sentinel = compactSentinelRef.current;
    if (!sentinel) return;

    const scrollRoot = getScrollRoot(sentinel);

    if (!scrollRoot) {
      return;
    }

    const syncInitialState = () => {
      const rootTop = scrollRoot.getBoundingClientRect().top;
      const sentinelBottom = sentinel.getBoundingClientRect().bottom;
      setIsCompact(sentinelBottom <= rootTop);
    };

    syncInitialState();

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;

        setIsCompact(current => {
          if (entry.intersectionRatio >= 1) {
            return false;
          }

          if (entry.intersectionRatio <= 0) {
            return true;
          }

          return current;
        });
      },
      {
        root: scrollRoot,
        threshold: [0, 1],
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scorecardLoading, sectionValues.length]);

  useLayoutEffect(() => {
    if (scorecardLoading) {
      return;
    }

    const compactHeader = compactHeaderRef.current;
    if (!compactHeader) return;

    const updateHeight = () => {
      setCompactHeaderHeight(Math.ceil(compactHeader.getBoundingClientRect().height));
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(compactHeader);

    return () => resizeObserver.disconnect();
  }, [scorecardLoading, sectionCards.length]);

  const compactScrollOffset =
    (compactHeaderHeight || COMPACT_HEADER_HEIGHT_FALLBACK_PX) + COMPACT_HEADER_GAP_PX;

  const beginEditingQuestion = (item: SectionQuestionResult) => {
    if (!item.question.metric_key || !item.finding) {
      return;
    }

    const existingReview = evaluation.reviews?.findings?.[item.question.metric_key];
    const existingAnswer = getReviewAnswer(existingReview, item.question.scoring_type);

    setDrafts(current => ({
      ...current,
      [item.question.metric_key!]: {
        metricKey: item.question.metric_key!,
        questionTitle:
          item.question.title ||
          item.question.label ||
          item.question.metric_key ||
          'Untitled Question',
        scoringType: item.question.scoring_type,
        answer: existingAnswer ?? getDefaultDraftAnswer(item.finding, item.question.scoring_type),
        evidence:
          existingReview?.evidence?.snippet || existingReview?.evidence?.snippets?.[0]?.text || '',
      },
    }));
    setActiveEvidenceMetricKey(item.question.metric_key);
  };

  const handleDraftAnswerChange = (metricKey: string, answer: DraftAnswerOption) => {
    setDrafts(current => ({
      ...current,
      [metricKey]: {
        ...current[metricKey],
        answer,
      },
    }));
  };

  const handleDraftEvidenceChange = (metricKey: string, evidence: string) => {
    setDrafts(current => ({
      ...current,
      [metricKey]: {
        ...current[metricKey],
        evidence,
      },
    }));
  };

  const markDraftEvidenceTouched = (metricKey: string) => {
    setDrafts(current => {
      const draft = current[metricKey];
      if (!draft || draft.evidenceTouched) {
        return current;
      }

      return {
        ...current,
        [metricKey]: {
          ...draft,
          evidenceTouched: true,
        },
      };
    });
  };

  const removeDraft = (metricKey: string) => {
    setDrafts(current => {
      const next = { ...current };
      delete next[metricKey];
      return next;
    });
    setActiveEvidenceMetricKey(current => (current === metricKey ? null : current));
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setDrafts({});
    setActiveEvidenceMetricKey(null);
    setIsConfirmDialogOpen(false);
  };

  const submitDrafts = () => {
    if (!evaluation.organization_id || !evaluation.id || draftEntries.length === 0) {
      return;
    }
    if (hasDraftValidationErrors) {
      return;
    }

    const findings = draftEntries.reduce<Record<string, EvaluationFindingReviewInput>>(
      (acc, draft) => {
        const currentFinding = evaluation.findings?.find(
          finding => finding.metric_key === draft.metricKey,
        );
        acc[draft.metricKey] = buildFindingReviewInput(draft, currentFinding);
        return acc;
      },
      {},
    );

    updateMutation.mutate(
      {
        organization_id: evaluation.organization_id,
        evaluation_id: evaluation.id,
        reviews: {
          findings,
        },
      },
      {
        onSuccess: () => {
          setIsConfirmDialogOpen(false);
          setIsEditing(false);
          setDrafts({});
          setActiveEvidenceMetricKey(null);
        },
      },
    );
  };

  const editAction = (
    <Button
      type="button"
      variant="outline"
      size="xs"
      className="gap-2"
      disabled={isWorkflowProcessing}
      onClick={() => setIsEditing(true)}
    >
      {isWorkflowProcessing ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Re-evaluating
        </>
      ) : (
        <>
          <PencilLine className="h-4 w-4" />
          Edit answers
        </>
      )}
    </Button>
  );

  const workflowBanner = <QcWorkflowStatusBanner evaluation={evaluation} className="mb-3" />;

  const editingToolbar = (
    <div className="sticky top-0 z-20 mb-2 border-b bg-background/95 px-4 py-2 backdrop-blur supports-backdrop-filter:bg-background/80">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <PencilLine className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-foreground">Editing evaluation</span>
          <Badge variant="secondary" className="h-5 px-1.5 text-[11px] font-medium">
            {draftEntries.length === 0
              ? 'No changes yet'
              : `${draftEntries.length} ${draftEntries.length === 1 ? 'change' : 'changes'} ready`}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={handleCancelEditing}>
            <X className="mr-1 h-4 w-4" />
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={draftEntries.length === 0 || updateMutation.isPending}
            onClick={() => {
              if (hasDraftValidationErrors) {
                draftEntries.forEach(draft => {
                  if (!draft.evidence.trim()) {
                    markDraftEvidenceTouched(draft.metricKey);
                  }
                });
                return;
              }
              setIsConfirmDialogOpen(true);
            }}
          >
            {updateMutation.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-1 h-4 w-4" />
            )}
            Update
          </Button>
        </div>
      </div>
    </div>
  );

  if (scorecardLoading) {
    return (
      <div className="space-y-3 px-4 py-4">
        <Skeleton className="h-20 rounded-md" />
        <Skeleton className="h-40 rounded-md" />
      </div>
    );
  }

  const expandCollapseButton = sectionResults.length > 0 && (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="h-7 w-7 shrink-0"
      aria-label={allSectionsExpanded ? 'Collapse all sections' : 'Expand all sections'}
      onClick={() => setOpenSections(allSectionsExpanded ? [] : sectionValues)}
    >
      {allSectionsExpanded ? (
        <ChevronsDownUp className="h-3.5 w-3.5" />
      ) : (
        <ChevronsUpDown className="h-3.5 w-3.5" />
      )}
    </Button>
  );

  const scorecardFilterBar =
    sectionResults.length > 0 ? (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
            <FunnelIcon className="h-3.5 w-3.5" />
            <span>Filter:</span>
          </div>
          <Button
            variant="outline"
            size="xs"
            aria-pressed={lostPointsFilter}
            onClick={() => setLostPointsFilter(prev => !prev)}
            className={cn(
              'rounded-full',
              lostPointsFilter
                ? 'border-critical-border bg-critical text-critical-foreground hover:bg-critical/80'
                : 'text-muted-foreground hover:bg-muted/50',
            )}
          >
            Lost Points ({filterCounts.lostPoints})
          </Button>
          <Button
            variant="outline"
            size="xs"
            aria-pressed={withFeedbackFilter}
            onClick={() => setWithFeedbackFilter(prev => !prev)}
            className={cn(
              'rounded-full',
              withFeedbackFilter
                ? 'border-attention-border bg-attention text-attention-foreground hover:bg-attention/80'
                : 'text-muted-foreground hover:bg-muted/50',
            )}
          >
            Has Feedback ({filterCounts.withFeedback})
          </Button>
          <Button
            variant="outline"
            size="xs"
            aria-pressed={reviewerChangesFilter}
            onClick={() => setReviewerChangesFilter(prev => !prev)}
            className={cn(
              'rounded-full',
              reviewerChangesFilter
                ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15'
                : 'text-muted-foreground hover:bg-muted/50',
            )}
          >
            Reviewer Changes ({filterCounts.reviewerChanges})
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{filterStatusText}</p>
      </div>
    ) : null;

  // AQ-653: ineligible calls are never scored, so there is no scorecard to show.
  // Replace the scorecard body with an "Ineligible" card that names the reason
  // (from reasons.metadata.ineligibility_reason, written by the eligibility agent).
  if (evaluation.ineligible) {
    const reasonTitle = formatIneligibilityReasonTitle(getIneligibilityReason(evaluation));
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <CircleOff className="h-8 w-8 text-muted-foreground" aria-hidden />
        <div className="flex flex-wrap items-center justify-center gap-2">
          <h3 className="text-base font-semibold text-foreground">{reasonTitle}</h3>
          <Badge variant="secondary" className="font-normal text-muted-foreground">
            Ineligible
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          This call was not scored, so there is no scorecard.
        </p>
      </div>
    );
  }

  return (
    <div>
      {isEditing ? (
        editingToolbar
      ) : (
        <>
          {workflowBanner}
          {!isWorkflowProcessing && (
            <QcScorecardHeader
              alerts={alerts}
              compactHeaderRef={compactHeaderRef}
              evaluation={evaluation}
              evaluationFeedbacks={evaluationFeedbacks}
              isCompact={isCompact}
              onScrollToSection={scrollToSection}
              sectionCards={sectionCards}
              userNameById={userNameById}
            />
          )}
        </>
      )}
      {/* Minimal-height sentinel: IO threshold=0 fires when this scrolls out of root bounds */}
      <div
        ref={compactSentinelRef}
        aria-hidden="true"
        className="shrink-0"
        style={{ height: '1px' }}
      />

      {/* Filter bar + Section heading */}
      {sectionResults.length > 0 && (
        <div className="px-4 pb-3">
          {scorecardFilterBar}
          <div className="mt-5 flex items-center justify-between gap-3">
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-foreground">Section Scorecard</h3>
              {evaluation.updated_at && (
                <div className="text-xs text-muted-foreground">
                  {lastUpdatedByName ? (
                    <>
                      Last updated by{' '}
                      <span className="font-semibold text-primary">{lastUpdatedByName}</span>
                      <span className="px-1.5 text-muted-foreground">·</span>
                    </>
                  ) : (
                    <>
                      Last updated
                      <span className="px-1.5 text-muted-foreground">·</span>
                    </>
                  )}
                  <FormattedDateTime value={evaluation.updated_at} />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && !isWorkflowProcessing && canEditEvaluation && editAction}
              {expandCollapseButton}
            </div>
          </div>
        </div>
      )}

      {/* Scrollable section content */}
      <div className="px-4 pb-4">
        {sectionResults.length === 0 ? (
          <div className="rounded-md border px-4 py-6 text-sm text-muted-foreground">
            No scorecard findings are available for this evaluation yet.
          </div>
        ) : (
          <Accordion
            type="multiple"
            value={openSections}
            onValueChange={value => setOpenSections(value as string[])}
            className="space-y-3"
          >
            {sectionResults.map((sectionResult, sectionIndex) => {
              const sectionValue = sectionResult.section.id || `section-${sectionIndex}`;
              const filteredQuestions = filteredQuestionsBySection[sectionIndex];
              const sectionWrapperClasses = 'rounded-md border bg-background';
              const scrollStyle = { scrollMarginTop: `${compactScrollOffset}px` };

              if (sectionResult.questionCount === 0) {
                return (
                  <div
                    id={sectionValue}
                    key={sectionValue}
                    className={sectionWrapperClasses}
                    style={scrollStyle}
                  >
                    <div className="px-4 py-3">
                      <div className="flex w-full items-center justify-between gap-3 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-semibold truncate text-muted-foreground">
                            {sectionResult.section.title || 'Untitled Section'}
                          </span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            0 questions
                          </span>
                        </div>
                        <span
                          className="shrink-0 text-sm font-semibold text-muted-foreground mx-2"
                          aria-label="No score available"
                        >
                          --
                        </span>
                      </div>
                    </div>
                    <div className="border-t px-4 py-6 text-center text-sm text-muted-foreground">
                      No Questions Activated for this category
                    </div>
                  </div>
                );
              }

              return (
                <div
                  id={sectionValue}
                  key={sectionValue}
                  className={sectionWrapperClasses}
                  style={scrollStyle}
                >
                  <AccordionItem value={sectionValue} className="border-none">
                    <AccordionTrigger className="px-4 py-3 text-left hover:no-underline">
                      <div className="flex w-full items-center justify-between gap-3 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-semibold truncate">
                            {sectionResult.section.title || 'Untitled Section'}
                          </span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {sectionResult.questionCount} questions
                          </span>
                        </div>
                        <div className="flex shrink-0 items-center gap-2 mx-2">
                          <span
                            className={cn(
                              'text-sm font-semibold',
                              sectionResult.score === undefined || sectionResult.score === null
                                ? 'text-muted-foreground'
                                : getQcScoreToneClasses(sectionResult.score),
                            )}
                          >
                            {getDisplayScore(sectionResult.score)}
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="border-t px-4 pb-4 pt-0">
                      {filteredQuestions.length === 0 && isAnyFilterActive && (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                          No questions match the current filters
                        </div>
                      )}
                      {filteredQuestions.map((item, itemIndex) => {
                        const question = item.question;
                        // effectiveFinding applies reviewer overrides on top of the stored AI finding.
                        // Missing raw_score is implicitly 0 because proto omits default-valued scalars.
                        const normalizedRawScore = item.effectiveFinding
                          ? normalizeQuestionRawScore(
                              item.effectiveFinding.raw_score,
                              question.scoring_type,
                            )
                          : undefined;
                        const answerLabel = getQuestionAnswerLabel(
                          item.finding,
                          question.scoring_type,
                        );
                        const scoreLabel = getQuestionScoreLabel(
                          item.effectiveFinding,
                          question.scoring_type,
                          question.weight ?? 1,
                        );
                        const questionHeading =
                          question.title ||
                          question.label ||
                          question.metric_key ||
                          'Untitled Question';
                        const questionSubtitle =
                          question.title && question.label && question.label !== questionHeading
                            ? question.label
                            : null;
                        const metricKey = question.metric_key || '';
                        const existingReview = metricKey
                          ? evaluation.reviews?.findings?.[metricKey]
                          : undefined;
                        const draft = metricKey ? drafts[metricKey] : undefined;
                        const evidenceSections = [
                          {
                            key: 'ai',
                            label: 'AI',
                            snippets: getFindingEvidenceSnippets(item.finding),
                          },
                          {
                            action:
                              isEditing && canEditEvaluation && existingReview && !draft ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="xs"
                                  className="h-6 gap-1.5 px-2 text-primary"
                                  onClick={() => beginEditingQuestion(item)}
                                >
                                  <PencilLine className="h-3.5 w-3.5" />
                                  Edit
                                </Button>
                              ) : undefined,
                            key: 'reviewer',
                            label: 'REVIEWER',
                            snippets: getFindingEvidenceSnippets(existingReview),
                          },
                        ];
                        const reviewerAnswer = getReviewAnswer(
                          existingReview,
                          question.scoring_type,
                        );
                        const showEvidenceError =
                          !!draft?.evidenceTouched && !draft.evidence.trim();

                        return (
                          <div
                            key={question.id || `${sectionIndex}-${itemIndex}`}
                            className={`flex gap-3 py-3 ${
                              itemIndex < filteredQuestions.length - 1 ? 'border-b' : ''
                            }`}
                          >
                            <div
                              className={`w-1.5 shrink-0 rounded-full ${getQuestionScoreBarClassName(normalizedRawScore)}`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 space-y-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <h4 className="text-sm font-semibold leading-snug text-foreground">
                                      {questionHeading}
                                    </h4>
                                    {question.prompt && (
                                      <SimpleTooltipV2
                                        content={
                                          <span className="block max-w-[calc(100vw-2rem)] whitespace-normal break-words text-left leading-relaxed sm:max-w-md">
                                            {question.prompt}
                                          </span>
                                        }
                                        side="bottom"
                                      >
                                        <Info className="h-3.5 w-3.5 shrink-0 cursor-help text-muted-foreground" />
                                      </SimpleTooltipV2>
                                    )}
                                    {question.is_critical && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs  border-critical-border text-critical-foreground"
                                      >
                                        Critical
                                      </Badge>
                                    )}
                                  </div>
                                  {questionSubtitle && (
                                    <p className="text-xs text-muted-foreground">
                                      {questionSubtitle}
                                    </p>
                                  )}
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  <span
                                    className={cn(
                                      WEIGHT_TEXT_CLASS,
                                      getQuestionScoreClassName(normalizedRawScore),
                                    )}
                                  >
                                    {scoreLabel}
                                  </span>
                                  {renderQuestionFeedbackFlag(
                                    evaluation.id,
                                    item.findingIndex,
                                    feedbacksByFindingIndex,
                                    userNameById,
                                  )}
                                </div>
                              </div>

                              {(answerLabel || reviewerAnswer || draft) && (
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  {answerLabel && (
                                    <>
                                      <span className="text-xs font-medium text-muted-foreground">
                                        AI
                                      </span>
                                      <Badge
                                        variant="secondary"
                                        className={cn(
                                          'text-xs',
                                          answerLabel === 'Yes'
                                            ? 'bg-positive text-positive-foreground'
                                            : answerLabel === 'Partial'
                                              ? 'bg-attention text-attention-foreground'
                                              : 'bg-critical text-critical-foreground',
                                        )}
                                      >
                                        {answerLabel}
                                      </Badge>
                                    </>
                                  )}
                                  {(reviewerAnswer || draft) && (
                                    <>
                                      {answerLabel && (
                                        <span className="text-xs text-muted-foreground">|</span>
                                      )}
                                      <span className="text-xs font-medium text-primary">
                                        REVIEWER
                                      </span>
                                      <Badge
                                        variant="outline"
                                        className={cn(
                                          'text-xs',
                                          getAnswerToneClass(
                                            draft?.answer || reviewerAnswer || 'no',
                                          ),
                                        )}
                                      >
                                        {getDraftAnswerLabel(
                                          draft?.answer || reviewerAnswer || 'no',
                                        )}
                                      </Badge>
                                    </>
                                  )}
                                </div>
                              )}

                              <EvidenceBlock
                                sections={evidenceSections}
                                onEvidenceSeek={onEvidenceSeek}
                              />

                              {isEditing && item.finding && metricKey && (
                                <div className="mt-3">
                                  {draft ? (
                                    <div className="rounded-md border border-primary/30 bg-background">
                                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-primary/20 px-3 py-2">
                                        <div className="text-sm font-medium">
                                          Provide your answer
                                        </div>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="xs"
                                          className="gap-1.5 text-muted-foreground"
                                          onClick={() => removeDraft(metricKey)}
                                        >
                                          <X className="h-3.5 w-3.5" />
                                          Cancel
                                        </Button>
                                      </div>
                                      <div className="space-y-3 px-3 py-3">
                                        <Tabs
                                          value={draft.answer}
                                          onValueChange={value =>
                                            handleDraftAnswerChange(
                                              metricKey,
                                              value as DraftAnswerOption,
                                            )
                                          }
                                        >
                                          <TabsList className="h-8 bg-muted/60">
                                            {getAnswerOptions(question.scoring_type).map(option => (
                                              <TabsTrigger
                                                key={option.value}
                                                value={option.value}
                                                className="h-6 px-2.5 text-xs"
                                              >
                                                {option.label}
                                              </TabsTrigger>
                                            ))}
                                          </TabsList>
                                        </Tabs>
                                        <div className="space-y-1.5">
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-medium text-foreground">
                                              Evidence{' '}
                                              <span className="text-critical-foreground">*</span>
                                            </span>
                                          </div>
                                          <QcEvidenceComposerInput
                                            value={draft.evidence}
                                            onFocus={() => {
                                              evidenceInputFocusedRef.current = true;
                                              setActiveEvidenceMetricKey(metricKey);
                                            }}
                                            onBlur={() => {
                                              evidenceInputFocusedRef.current = false;
                                              markDraftEvidenceTouched(metricKey);
                                              if (!transcriptPointerDownRef.current) {
                                                setActiveEvidenceMetricKey(current =>
                                                  current === metricKey ? null : current,
                                                );
                                              }
                                            }}
                                            onChange={event =>
                                              handleDraftEvidenceChange(
                                                metricKey,
                                                event.target.value,
                                              )
                                            }
                                            placeholder="Why this answer? Quote specifics from the call where relevant."
                                            className={cn(
                                              showEvidenceError &&
                                                'border-critical-border focus-within:ring-critical-foreground/30',
                                            )}
                                            textareaClassName="min-h-24"
                                          />
                                          {showEvidenceError && (
                                            <p className="text-[11px] text-critical-foreground">
                                              Evidence is required.
                                            </p>
                                          )}
                                          <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                                            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                            <span>
                                              Tip: Click any timestamp in the transcript, or type{' '}
                                              <span className="font-medium text-primary">
                                                @4:13
                                              </span>{' '}
                                              to reference a specific moment in evidence.
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    canEditEvaluation &&
                                    !existingReview && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="xs"
                                        className="gap-1.5 rounded-full"
                                        onClick={() => beginEditingQuestion(item)}
                                      >
                                        <CircleOff className="h-3.5 w-3.5" />
                                        Disagree?
                                      </Button>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </AccordionContent>
                  </AccordionItem>
                </div>
              );
            })}
          </Accordion>
        )}
      </div>
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Update evaluation</DialogTitle>
            <DialogDescription>
              You&apos;re adding {draftEntries.length} reviewer{' '}
              {draftEntries.length === 1 ? 'answer' : 'answers'}. Confirming will recompute section
              scores, overall score, and call-level outcomes against the new reviewer answers.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border">
            {draftEntries.map((draft, index) => (
              <div
                key={draft.metricKey}
                className={cn(
                  'flex items-center justify-between gap-3 px-4 py-3',
                  index < draftEntries.length - 1 && 'border-b',
                )}
              >
                <span className="text-sm font-medium">{draft.questionTitle}</span>
                <Badge variant="secondary" className={getAnswerToneClass(draft.answer)}>
                  {getDraftAnswerLabel(draft.answer)}
                </Badge>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">Updating may take a moment.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button disabled={updateMutation.isPending} onClick={submitDrafts}>
              {updateMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Update
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
